import { ChevronLeft, Package, ShoppingCart, Facebook, Twitter, MessageSquare, Share2, Minus, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container } from '@/components/layout/container';
import { ResponsiveImage } from '@/components/ui/responsive-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { isApprovedProductAsset } from '@/lib/approved-product-images';
import { normalizeProductImages } from '@/lib/image-utils';
import { useProduct } from '@/features/products/hooks/use-products';
import { useCartMutations } from '@/features/cart/hooks/use-cart-mutations';
import { useProductInCart } from '@/features/cart/hooks/use-cart';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useShowPrices, useUnlimitedStock, useSettings } from '@/features/settings/hooks/use-settings';
import { trackProductVisit } from '@/features/analytics/api/visits';
import type { ProductVariant } from '@/features/products/types';

const UNLIMITED_STOCK_DISPLAY_THRESHOLD = 999999;

export default function ProductDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId = '' } = useParams();
  const { user } = useAuth();
  const { data: product, isLoading, error } = useProduct(productId);
  const { addToCart, removeFromCart, updateQuantity, isAdding, isRemoving, isUpdating } = useCartMutations(user?.id);
  const showPrices = useShowPrices();
  const unlimitedStock = useUnlimitedStock();
  const { settings } = useSettings();
  const waNumber = settings.whatsapp_business_number;
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>(undefined);
  const [desiredQuantity, setDesiredQuantity] = useState(1);

  useEffect(() => {
    if (productId) {
      void trackProductVisit(productId);
    }
  }, [productId]);

  useEffect(() => {
    if (!product) {
      return;
    }

    setSelectedVariant(
      product.variants.find((variant) => variant.isDefault && (unlimitedStock || variant.stock > 0)) ||
        product.variants.find((variant) => unlimitedStock || variant.stock > 0) ||
        product.variants.find((variant) => variant.isDefault) ||
        product.variants[0]
    );
  }, [product, unlimitedStock]);

  const images = useMemo(() => {
    if (!product) {
      return [];
    }

    return normalizeProductImages(undefined, product.images);
  }, [product]);
  const primaryImage = images[0];
  const usesApprovedProductAsset = isApprovedProductAsset(primaryImage);
  const { isInCart: isSelectedVariantInCart, quantity: selectedVariantCartQuantity, item: selectedCartItem } = useProductInCart(
    product?.id ?? productId,
    selectedVariant?.id
  );
  const shouldHighlightCartConfigurator = Boolean(
    (location.state as { focusCartConfigurator?: boolean } | null)?.focusCartConfigurator
  );

  useEffect(() => {
    if (!product) {
      return;
    }

    const requestedVariantId = (location.state as { selectedVariantId?: string } | null)?.selectedVariantId;
    if (!requestedVariantId) {
      return;
    }

    const matchingVariant = product.variants.find((variant) => variant.id === requestedVariantId);
    if (matchingVariant) {
      setSelectedVariant(matchingVariant);
    }
  }, [location.state, product]);

  useEffect(() => {
    setDesiredQuantity(selectedVariantCartQuantity > 0 ? selectedVariantCartQuantity : 1);
  }, [selectedVariant?.id, selectedVariantCartQuantity]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-50 py-12">
        <Container>
          <p className="text-brand-600">Loading product...</p>
        </Container>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-brand-50 py-12">
        <Container>
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <h1 className="text-2xl font-semibold text-brand-900">Product not found</h1>
              <p className="text-brand-600">{error instanceof Error ? error.message : 'We could not load this product.'}</p>
              <Button onClick={() => navigate('/catalog')}>Back to Catalog</Button>
            </CardContent>
          </Card>
        </Container>
      </div>
    );
  }

  const displayPrice = selectedVariant?.price ?? product.price;
  const comparePrice = selectedVariant?.compareAtPrice ?? product.compareAtPrice;
  const stock = selectedVariant?.stock ?? product.stockQuantity;
  const shouldHideStockCount =
    unlimitedStock || stock >= UNLIMITED_STOCK_DISPLAY_THRESHOLD;
  const outOfStock = unlimitedStock ? false : stock <= 0;
  const maxSelectableQuantity = unlimitedStock ? Number.MAX_SAFE_INTEGER : Math.max(stock, 1);
  const isPriceMissing = !(Number.isFinite(displayPrice) && displayPrice > 0);
  const showAskForPrice = !showPrices || isPriceMissing;
  const askForPriceUrl = waNumber
    ? `https://wa.me/${waNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi, I'd like to know the price of ${product.name}`)}`
    : undefined;
  const selectedVariantLabel = selectedVariant?.volume || 'Default option';
  const cartActionPending = isAdding || isRemoving || isUpdating;
  const hasQuantityChange = selectedVariantCartQuantity !== desiredQuantity;

  const changeDesiredQuantity = (nextQuantity: number) => {
    const cappedQuantity = Math.min(Math.max(nextQuantity, 1), maxSelectableQuantity);
    setDesiredQuantity(cappedQuantity);
  };

  const handleSaveQuantity = async () => {
    if (outOfStock) {
      return;
    }

    if (isPriceMissing) {
      alert('This item does not have a price yet. Please contact support.');
      return;
    }

    if (selectedCartItem) {
      await updateQuantity({
        itemId: selectedCartItem.id,
        quantity: desiredQuantity,
        customerId: user?.id,
      });
      return;
    }

    await addToCart({
      product,
      quantity: desiredQuantity,
      variant: selectedVariant,
    });
  };

  const handleRemoveFromCart = async () => {
    if (!selectedCartItem) {
      return;
    }

    await removeFromCart({
      itemId: selectedCartItem.id,
      customerId: user?.id,
    });
  };

  return (
    <div className="min-h-screen bg-brand-50 py-4 sm:py-8">
      <Container>
        <Button variant="ghost" className="mb-4 sm:mb-6" onClick={() => navigate(-1)}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
          <Card className="overflow-hidden">
            <div className="aspect-square bg-brand-50">
              <ResponsiveImage
                src={primaryImage}
                fallbackImages={images.slice(1)}
                alt={product.name}
                productName={product.name}
                category={product.categoryName}
                aspectRatio="1/1"
                objectFit={usesApprovedProductAsset ? 'contain' : 'cover'}
                priority="high"
                className={usesApprovedProductAsset ? 'bg-white' : undefined}
                imageClassName={usesApprovedProductAsset ? 'bg-white scale-[1.08]' : undefined}
              />
            </div>
          </Card>

          <Card>
            <CardContent className="space-y-6 p-4 sm:p-6">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {product.categoryName && <Badge variant="secondary">{product.categoryName}</Badge>}
                  {product.isPopular && <Badge variant="info">Popular</Badge>}
                  {product.isNewArrival && <Badge variant="success">New</Badge>}
                  {product.isOnSale && <Badge variant="warning">On Offer</Badge>}
                </div>
                <div>
                  <h1 className="text-3xl font-display font-bold text-brand-900">{product.name}</h1>
                  {product.brand && <p className="mt-1 text-brand-500">{product.brand}</p>}
                </div>
                {product.description && (
                  <p className="text-brand-700 leading-relaxed">{product.description}</p>
                )}
              </div>

              {product.variants.length > 1 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-brand-900">Choose size</p>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((variant) => {
                      const variantOutOfStock = unlimitedStock ? false : variant.stock <= 0;
                      return (
                        <button
                          key={variant.id}
                          type="button"
                          onClick={() => setSelectedVariant(variant)}
                          disabled={variantOutOfStock}
                          className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                            selectedVariant?.id === variant.id
                              ? 'border-brand-600 bg-brand-600 text-white'
                              : variantOutOfStock
                              ? 'border-brand-100 bg-brand-50 text-brand-300 cursor-not-allowed'
                              : 'border-brand-200 bg-white text-brand-700 hover:border-brand-400'
                          }`}
                        >
                          {variant.volume}{variantOutOfStock ? ' (out)' : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className={`space-y-5 rounded-2xl bg-white p-4 shadow-sm ring-1 sm:p-5 ${shouldHighlightCartConfigurator ? 'ring-brand-300 shadow-md' : 'ring-brand-100'}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                  {showAskForPrice ? (
                    askForPriceUrl ? (
                      <a
                        href={askForPriceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-base font-semibold text-green-700 ring-1 ring-green-200 transition-colors hover:bg-green-100"
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.556 4.122 1.528 5.855L0 24l6.293-1.504A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.007-1.371l-.36-.213-3.727.891.924-3.638-.234-.373A9.818 9.818 0 1112 21.818z"/></svg>
                        Ask for price
                      </a>
                    ) : (
                      <span className="text-brand-500 italic">Contact for pricing</span>
                    )
                  ) : (
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-2xl font-semibold text-brand-900 sm:text-3xl">KES {displayPrice.toLocaleString()}</span>
                      {comparePrice && comparePrice > displayPrice && (
                        <span className="text-brand-400 line-through">KES {comparePrice.toLocaleString()}</span>
                      )}
                    </div>
                  )}
                  <p className="mt-2 flex items-center gap-2 text-sm text-brand-600">
                    <Package className="h-4 w-4" />
                    {outOfStock ? 'Currently out of stock' : shouldHideStockCount ? 'Available' : `${stock} available now`}
                  </p>
                  </div>

                  {isSelectedVariantInCart && (
                    <Badge variant="success" className="w-fit whitespace-nowrap">
                      {selectedVariantCartQuantity} in cart
                    </Badge>
                  )}
                </div>

                <div className="rounded-2xl border border-brand-100 bg-brand-50/70 p-4">
                  <div className="flex flex-col gap-4">
                    <div>
                      <p className="text-sm font-semibold text-brand-900">Choose quantity</p>
                      <p className="mt-1 text-sm text-brand-600">
                        {selectedVariantLabel} {isSelectedVariantInCart ? 'is already in your cart. Update the amount here.' : 'can be added to your cart from here.'}
                      </p>
                    </div>

                    <div className="flex items-center justify-between gap-3 sm:max-w-[220px]">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => changeDesiredQuantity(desiredQuantity - 1)}
                        disabled={desiredQuantity <= 1 || cartActionPending}
                        className="h-11 w-11 flex-shrink-0"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="min-w-0 flex-1 rounded-xl border border-brand-200 bg-white px-4 py-2 text-center">
                        <span className="text-2xl font-semibold text-brand-900">{desiredQuantity}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => changeDesiredQuantity(desiredQuantity + 1)}
                        disabled={desiredQuantity >= maxSelectableQuantity || outOfStock || cartActionPending}
                        className="h-11 w-11 flex-shrink-0"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {!unlimitedStock && !outOfStock && (
                    <p className="mt-3 text-xs text-brand-500">
                      Maximum available right now: {stock}
                    </p>
                  )}
                </div>

                <div className="grid gap-3">
                  <Button
                    onClick={() => void handleSaveQuantity()}
                    disabled={isPriceMissing || outOfStock || cartActionPending || (isSelectedVariantInCart && !hasQuantityChange)}
                    className="h-auto min-h-12 whitespace-normal px-4 py-3 text-center"
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    {isPriceMissing
                      ? 'Price unavailable'
                      : outOfStock
                      ? 'Out of Stock'
                      : isSelectedVariantInCart
                        ? hasQuantityChange
                          ? `Update cart to ${desiredQuantity}`
                          : 'Already in cart'
                        : `Add ${desiredQuantity} to cart`}
                  </Button>

                  {isSelectedVariantInCart && (
                    <Button
                      variant="outline"
                      onClick={() => void handleRemoveFromCart()}
                      disabled={cartActionPending}
                      className="h-auto min-h-11 whitespace-normal"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/cart')}
                      className="h-auto min-h-11 whitespace-normal"
                    >
                      View cart
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => navigate('/catalog')}
                      className="h-auto min-h-11 whitespace-normal"
                    >
                      Continue shopping
                    </Button>
                  </div>
                </div>
              </div>

              {/* Social Sharing Section */}
              {settings.social_show_share_buttons && (
                <div className="pt-6 border-t border-brand-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-brand-600">
                    <Share2 className="h-4 w-4" />
                    <span>Share this product</span>
                  </div>
                  <div className="flex gap-3">
                    {/* WhatsApp */}
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`Check out ${product.name} on Karebe: ${window.location.href}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-all hover:scale-110 shadow-sm"
                      title="Share on WhatsApp"
                    >
                      <MessageSquare className="h-5 w-5" />
                    </a>
                    {/* Facebook */}
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all hover:scale-110 shadow-sm"
                      title="Share on Facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                    {/* Twitter / X */}
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${product.name} on Karebe`)}&url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 transition-all hover:scale-110 shadow-sm"
                      title="Share on Twitter"
                    >
                      <Twitter className="h-5 w-5" />
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </Container>
    </div>
  );
}
